import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { useParams, Outlet } from 'react-router-dom';
// Removed useRouteBreadcrumbs import to prevent re-renders on navigation
import StaticSiteConfig from '../components/StaticSiteConfig';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  LocationOn,
  EvStation,
  MyLocation,
  ZoomIn,
  ZoomOut,
  Fullscreen,
} from '@mui/icons-material';
import ChargerNavigationBar from '../components/ChargerNavigationBar';
import { gpsService, chargersService } from '../services/api';
import { GPSData, ChargerUnit } from '../types';
import { getRelativeTime } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { theme } from '../theme';

// Mapbox access token - should be stored in environment variable
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Validate the access token format
const isValidMapboxToken = (token: string | undefined): boolean => {
  return !!(token && token.startsWith('pk.') && token.length > 50);
};

// Set access token only if valid
if (MAPBOX_ACCESS_TOKEN && isValidMapboxToken(MAPBOX_ACCESS_TOKEN)) {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
} else {
  console.warn('Invalid or missing Mapbox access token. Map functionality will be limited.');
}

// Custom marker component matching Map.tsx
const EvStationMarker: React.FC = () => {
  return (
    <div
      style={{
        width: '40px',
        height: '40px',
        backgroundColor: theme.palette.secondary.dark,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      }}
    >
      <EvStation style={{ fontSize: '24px' }} />
    </div>
  );
};

// Using real ChargerUnit type from API - no mock data needed


// Memoized static content component to prevent re-renders
const ChargerStaticContent = React.memo<{
  chargerDetail: ChargerUnit;
  user: any;
  orgId: string | undefined;
  mapContainer: React.RefObject<HTMLDivElement>;
  map: React.RefObject<mapboxgl.Map | null>;
  mapError: string | null;
  mapInitializationTimeout: boolean;
  hasValidToken: boolean;
  loadingGPS: boolean;
  gpsError: string | null;
  gpsData: GPSData[];
  gpsTimestamp: string | null;
  mapControls: Array<{ icon: React.ReactNode; action: string }>;
  handleMapControl: (action: string) => void;
  addMarkersToMap: () => void;
  onMapRetry: () => void;
}>(({ 
  chargerDetail, 
  user, 
  orgId,
  mapContainer, 
  map, 
  mapError, 
  mapInitializationTimeout, 
  hasValidToken, 
  loadingGPS, 
  gpsError, 
  gpsData, 
  gpsTimestamp, 
  mapControls, 
  handleMapControl,
  addMarkersToMap,
  onMapRetry
}) => {
  return (
    <>
      {/* Battery header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: (theme) => theme.palette.success.main, fontSize: 16 }} />
          <Box sx={{ position: 'relative' }}>
            <EvStation sx={{ fontSize: 32, color: 'rgba(0,0,0,0.6)' }} />
          </Box>
        </Box>
        
        <Box>
          <Typography variant="h4" sx={{ 
            fontSize: '18px',
            fontFamily: 'Montserrat',
            fontWeight: 500,
            color: 'rgba(0,0,0,0.87)',
            lineHeight: '27px',
            marginBottom: 0
          }}>
            {chargerDetail.unit_name || 'N/A'}
          </Typography>
          <Typography sx={{
            color: 'rgba(0, 0, 0, 0.60)',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '157%',
            letterSpacing: '0.1px',
          }}>
            {[chargerDetail.vendor, chargerDetail.model].filter(Boolean).join(' ')}
          </Typography>
        </Box>
      </Box>

      {/* Flow diagram and map */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Flow diagram */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            height: 397,
            borderRadius: 1,
            boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <StaticSiteConfig 
                deviceType="charger"
                deviceId={chargerDetail.unit_id}
                deviceName={chargerDetail.unit_name}
                modelName={chargerDetail.model}
                orgId={orgId}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Map card */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            height: 397,
            borderRadius: 1,
            boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* Map container */}
            <Box sx={{ 
              width: '100%',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div 
                ref={mapContainer} 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  minHeight: '320px',
                  position: 'relative',
                  backgroundColor: '#f5f5f5', // Fallback background
                  border: '1px solid #ddd', // Debug border
                }} 
                onLoad={() => console.log('Map container div loaded')}
              />
              
              {/* Loading indicator for map */}
              {!map.current && !mapError && !mapInitializationTimeout && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 5,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              {/* Fallback map placeholder if initialization fails */}
              {!map.current && (mapError || mapInitializationTimeout) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 5,
                    textAlign: 'center',
                    padding: 2,
                  }}
                >
                  <LocationOn sx={{ fontSize: 48, color: 'rgba(0,0,0,0.3)' }} />
                  <Typography variant="body2" sx={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)' }}>
                    Map unavailable
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.4)' }}>
                    {mapError || 'Initialization failed'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.4)' }}>
                    GPS data will still be shown below
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onMapRetry}
                    sx={{ mt: 1, fontSize: '12px' }}
                  >
                    Retry Map
                  </Button>
                </Box>
              )}

              {/* Error Display */}
              {mapError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity={hasValidToken ? "error" : "warning"}
                    sx={{ fontSize: '12px' }}
                  >
                    {mapError}
                  </Alert>
                </Box>
              )}

              {/* GPS Error Display */}
              {gpsError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity="error"
                    sx={{ fontSize: '12px' }}
                  >
                    GPS Position Unavailable: {gpsError}
                  </Alert>
                </Box>
              )}

              {/* No GPS Data Available */}
              {!loadingGPS && !gpsError && gpsData.length === 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity="warning"
                    sx={{ fontSize: '12px' }}
                  >
                    No GPS position data available for this charger
                  </Alert>
                </Box>
              )}

              {/* GPS Loading Indicator */}
              {loadingGPS && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'white',
                    padding: 1,
                    borderRadius: 1,
                    boxShadow: 2,
                  }}
                >
                  <CircularProgress size={16} />
                  <span style={{ fontSize: '12px' }}>Loading positions...</span>
                </Box>
              )}

              {/* Custom Map Controls */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                {mapControls.map((control, index) => (
                  <IconButton
                    key={index}
                    onClick={() => handleMapControl(control.action)}
                    size="small"
                    sx={{
                      bgcolor: 'white',
                      boxShadow: 2,
                      width: 32,
                      height: 32,
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    {control.icon}
                  </IconButton>
                ))}
              </Box>

              {/* Location info overlay */}
              <Box sx={{ 
                position: 'absolute', 
                bottom: 8, 
                right: 8, 
                display: 'flex', 
                gap: 1,
              }}>
                <Tooltip 
                  title={gpsTimestamp ? new Date(gpsTimestamp).toLocaleString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  }) : "GPS timestamp not available"}
                  arrow
                >
                  <Chip
                    icon={<LocationOn sx={{ fontSize: 16, color: 'white !important' }} />}
                    label={gpsTimestamp ? `${getRelativeTime(gpsTimestamp)} (GPS)` : "Loading GPS..."}
                    size="small"
                    sx={{
                      backgroundColor: (theme) => theme.palette.secondary.dark,
                      color: 'white',
                      fontSize: '12px',
                      fontFamily: 'Montserrat',
                      height: 24,
                      '& .MuiChip-label': {
                        fontSize: '12px',
                        lineHeight: '16px',
                        letterSpacing: '0.16px',
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
            
            {/* Map content */}
            {/* <CardContent sx={{ p: 2, height: 77, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                First line: Current location
                <Typography sx={{ 
                  fontSize: '14px',
                  fontFamily: 'Montserrat',
                  color: 'rgba(0,0,0,0.87)',
                }}>
                  Current location: Location data not available
                </Typography>
                
                Second line: ongoing-icon, address, dates, alias
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      
                  <Typography sx={{ 
                    fontSize: '14px',
                    fontFamily: 'Montserrat',
                    color: (theme) => theme.palette.primary.light,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}>
                    Allocation data not available
                  </Typography>
                  <Typography sx={{ 
                    fontSize: '14px',
                    fontFamily: 'Montserrat',
                    color: 'rgba(0,0,0,0.6)',
                  }}>
                    Period data not available
                  </Typography>
                  <Typography sx={{ 
                    fontSize: '14px',
                    fontFamily: 'Montserrat',
                    color: 'rgba(0,0,0,0.6)',
                  }}>
                  </Typography>
                </Box>
              </Box>
            </CardContent> */}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
});

const ChargerDetails: React.FC = () => {
  const { unitId } = useParams<{ unitId: string }>();
  const { setChargerBreadcrumbs } = useBreadcrumbs();
  const { user, isLoading: authLoading } = useAuth();
  const orgId = user?.orgId; // Get orgId from user context, not URL params
  const [chargerDetail, setChargerDetail] = useState<ChargerUnit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Map-related state
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const chargerDataLoaded = useRef<boolean>(false);
  // Refs to prevent duplicate API calls in StrictMode
  const chargerDetailFetchedRef = useRef<boolean>(false);
  const gpsDataFetchedRef = useRef<boolean>(false);
  const lastFetchedUnitId = useRef<string | null>(null);
  // Removed coordinate state variables to prevent re-initialization
  const [mapError, setMapError] = useState<string | null>(null);
  const [hasValidToken] = useState(isValidMapboxToken(MAPBOX_ACCESS_TOKEN));
  const [gpsData, setGpsData] = useState<GPSData[]>([]);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [gpsTimestamp, setGpsTimestamp] = useState<string | null>(null);
  const [mapInitializationTimeout, setMapInitializationTimeout] = useState(false);
  
  // Removed useRouteBreadcrumbs to prevent re-renders on navigation
  // Breadcrumbs will be handled by the parent layout or context

  // Map controls configuration - memoized to prevent re-renders
  const mapControls = useMemo(() => [
    { icon: <ZoomIn />, action: 'zoomIn' },
    { icon: <ZoomOut />, action: 'zoomOut' },
    { icon: <MyLocation />, action: 'location' },
    { icon: <Fullscreen />, action: 'fullscreen' },
  ], []);

  // Function to fetch GPS data
  const fetchGPSData = useCallback(async () => {
    // Check if we have the required IDs
    if (!unitId || !orgId) {
      console.log('Missing unitId or orgId, cannot fetch GPS data');
      setGpsError('Missing required information to fetch GPS data');
      setLoadingGPS(false);
      return;
    }
    
    // Prevent duplicate calls - check if already fetching
    if (loadingGPS) {
      console.log('GPS data fetch already in progress, skipping');
      return;
    }
    
    // Only fetch if we don't have GPS data for this unitId
    if (gpsData.length > 0 && !gpsError && lastFetchedUnitId.current === unitId) {
      console.log('GPS data already available for this unitId, skipping fetch');
      return;
    }
    
    setLoadingGPS(true);
    setGpsError(null);
    
    try {
      console.log('Fetching GPS data for unit:', unitId, 'org:', orgId);
      const response = await gpsService.getUnitGPS(unitId, orgId);
      console.log('GPS API response:', response);
      
      if (response.success && response.data && response.data.data) {
        console.log('GPS data received:', response.data.data);
        setGpsData(response.data.data);
        
        // Set the latest GPS timestamp
        if (response.data.data.length > 0) {
          const latestLocation = response.data.data[0]; // Assuming the first item is the latest
          setGpsTimestamp(latestLocation.timestamp);
        }
        
        // If we have GPS data, center the map on the first location
        if (response.data.data.length > 0) {
          const firstLocation = response.data.data[0];
          console.log('Centering map on charger location:', firstLocation);
          // Update map center directly without changing state
          if (map.current) {
            // If map is already loaded, fly to location
            if (map.current.loaded()) {
              map.current.flyTo({
                center: [firstLocation.longitude, firstLocation.latitude],
                zoom: 12
              });
            } else {
              // If map is not loaded yet, wait for it to load then set center
              map.current.once('load', () => {
                if (map.current) {
                  map.current.flyTo({
                    center: [firstLocation.longitude, firstLocation.latitude],
                    zoom: 12
                  });
                }
              });
            }
          } else {
            // If map doesn't exist yet, it will be initialized with this location
            console.log('Map not initialized yet, will use GPS location for initial center');
          }
        }
        
        // Don't set loadingGPS to false here - wait until markers are added
        // The addMarkersToMap function will handle hiding the loading indicator
      } else {
        console.error('GPS API failed:', response.message);
        setGpsError(response.message || 'Failed to fetch GPS data');
        setGpsData([]);
        setLoadingGPS(false);
      }
    } catch (error) {
      console.error('GPS fetch error:', error);
      setGpsError(error instanceof Error ? error.message : 'Unknown error occurred');
      setGpsData([]);
      // Reset ref on error so we can retry if needed
      gpsDataFetchedRef.current = false;
      setLoadingGPS(false);
    }
  }, [unitId, orgId, gpsData, loadingGPS, gpsError]);

  // Function to add markers to the map
  const addMarkersToMap = useCallback(() => {
    console.log('=== ADDING MARKERS TO MAP ===');
    console.log('map.current:', map.current);
    console.log('gpsData:', gpsData);
    console.log('gpsData.length:', gpsData?.length);
    
    if (!map.current || !gpsData || gpsData.length === 0) {
      console.log('❌ Cannot add markers - missing map or GPS data');
      return;
    }

    console.log('✅ Adding markers to map...');

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add new markers
    gpsData.forEach((location, index) => {
      console.log(`Adding marker ${index + 1} at:`, location.longitude, location.latitude);
      
      // Create custom EvStation icon element using React (matching Map.tsx)
      const el = document.createElement('div');
      const root = createRoot(el);
      root.render(<EvStationMarker />);
      
      const marker = new mapboxgl.Marker(el)
        .setLngLat([location.longitude, location.latitude])
        .addTo(map.current!);

      // Get unit name from charger detail or GPS data
      const unitName = chargerDetail?.unit_name || location.unit_name || `Unit ${index + 1}`;

      // Add popup with location info (matching Map.tsx design without GO TO button)
      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: true,
        maxWidth: '320px' // Prevent overflow while allowing a bit more width
      }).setHTML(`
        <div style="
          box-sizing: border-box;
          min-width: 240px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        ">
          <!-- Top Section: Asset Name -->
          <div style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 0;
            width: 100%;
          ">
            <div style="display: flex; align-items: center; gap: 8px;">
              <img 
                src="${process.env.PUBLIC_URL}/ev_station.svg" 
                alt="EV Station" 
                style="
                  width: 32px;
                  height: 32px;
                  flex-shrink: 0;
                  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
                "
              />
              <span style="font-size: 16px; font-weight: 600; color: #666666;">
                ${unitName}
              </span>
            </div>
          </div>
          
          <!-- Divider -->
          <div style="
            width: 100%;
            height: 1px;
            background-color: #B5AFA9;
            margin: 12px 0;
          "></div>
          
          <!-- Bottom Section: GPS Location (Lat/Lng) -->
          <div style="
            display: flex;
            flex-direction: row;
            gap: 4px;
            font-size: 12px;
            font-family: 'Montserrat', sans-serif;
            color: rgba(0, 0, 0, 0.87);
          ">
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="font-weight: 600; color: rgba(0, 0, 0, 0.6);">Lat:</span>
              <span style="color: rgba(0, 0, 0, 0.87);">${location.latitude.toFixed(6)}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="font-weight: 600; color: rgba(0, 0, 0, 0.6);">Lng:</span>
              <span style="color: rgba(0, 0, 0, 0.87);">${location.longitude.toFixed(6)}</span>
            </div>
          </div>
        </div>
      `);

      marker.setPopup(popup);
      markers.current.push(marker);
    });
    
    console.log(`✅ Added ${markers.current.length} markers to map`);
    
    // Hide loading indicator once markers are placed
    if (markers.current.length > 0) {
      setLoadingGPS(false);
    }
  }, [gpsData, chargerDetail]);

  const handleMapControl = useCallback((action: string) => {
    if (!map.current) return;

    switch (action) {
      case 'zoomIn':
        map.current.zoomIn();
        break;
      case 'zoomOut':
        map.current.zoomOut();
        break;
      case 'location':
        // Get user's current location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { longitude, latitude } = position.coords;
              map.current!.flyTo({
                center: [longitude, latitude],
                zoom: 14
              });
            },
            (error) => {
              console.error('Error getting location:', error);
            }
          );
        }
        break;
      case 'fullscreen':
        // Toggle fullscreen
        if (mapContainer.current) {
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            mapContainer.current.requestFullscreen();
          }
        }
        break;
    }
  }, []);

  const handleMapRetry = useCallback(() => {
    console.log('Manual map retry triggered');
    setMapError(null);
    setMapInitializationTimeout(false);
    // Force re-initialization by clearing the map reference
    if (map.current) {
      map.current.remove();
      map.current = null;
    }
    // Trigger re-initialization
    setTimeout(() => {
      const container = mapContainer.current;
      if (container && hasValidToken) {
        console.log('Retrying map initialization...');
        try {
          // Use GPS data if available, otherwise use a temporary center
          let retryCenter: [number, number] = [0, 0];
          let retryZoom = 12;
          
          if (gpsData && gpsData.length > 0) {
            const firstLocation = gpsData[0];
            retryCenter = [firstLocation.longitude, firstLocation.latitude];
            console.log('Using charger GPS location for retry center:', retryCenter);
          }
          
          map.current = new mapboxgl.Map({
            container: container,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: retryCenter,
            zoom: retryZoom,
            attributionControl: false // Disable Mapbox attribution
          });
          map.current.on('load', () => {
            console.log('Map retry successful');
            setMapError(null);
            setMapInitializationTimeout(false);
          });
        } catch (error) {
          console.error('Map retry failed:', error);
          setMapError('Map retry failed');
        }
      }
    }, 100);
  }, [hasValidToken, gpsData]);

  // Fetch charger detail when unitId or orgId changes
  useEffect(() => {
    if (!unitId || !orgId) {
      console.log('Missing required params - unitId:', unitId, 'orgId:', orgId);
      setLoading(false);
      if (!orgId) {
        setError('Organization ID not found');
      }
      return;
    }

    // If unitId changed, clear stale data immediately
    if (lastFetchedUnitId.current !== unitId) {
      console.log('UnitId changed from', lastFetchedUnitId.current, 'to', unitId, '- clearing stale data');
      setChargerDetail(null); // Clear stale data immediately
      setError(null);
      // Reset refs to allow fetching new data
      chargerDataLoaded.current = false;
      chargerDetailFetchedRef.current = false;
      gpsDataFetchedRef.current = false;
      // Clear GPS data
      setGpsData([]);
      setGpsTimestamp(null);
      setGpsError(null);
    }

    // Skip if we already fetched this specific unitId (prevents duplicate calls in StrictMode)
    if (lastFetchedUnitId.current === unitId && chargerDataLoaded.current) {
      console.log('Charger detail already loaded for unitId:', unitId);
      return;
    }

    const fetchChargerDetail = async () => {
      // Prevent duplicate calls within the same effect execution
      if (chargerDetailFetchedRef.current && lastFetchedUnitId.current === unitId) return;
      chargerDetailFetchedRef.current = true;
      lastFetchedUnitId.current = unitId;

      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching charger detail for unitId:', unitId, 'orgId:', orgId);
        const response = await chargersService.getCharger(orgId, unitId);
        
        console.log('API Response:', response);
        console.log('Response success:', response.success);
        console.log('Response data:', response.data);
        console.log('Response data.units:', response.data?.units);
        console.log('Response data.units length:', response.data?.units?.length);
        
        if (response.success && response.data && response.data.units && response.data.units.length > 0) {
          // Find the unit that matches the requested unitId
          const units = response.data.units;
          const charger = units.find(u => u.unit_id === unitId);
          if (charger) {
            console.log('Charger detail loaded:', charger);
            console.log('Setting charger detail - unit_name:', charger.unit_name, 'vendor:', charger.vendor, 'model:', charger.model);
            setChargerDetail(charger);
            chargerDataLoaded.current = true;
          } else {
            console.error(`API returned units but none match unitId: ${unitId}`);
            setChargerDetail(null);
            setError(`Charger ${unitId} not found in API response`);
          }
        } else {
          console.error('No charger found for unitId:', unitId);
          console.error('Response details:', {
            success: response.success,
            hasData: !!response.data,
            hasUnits: !!response.data?.units,
            unitsLength: response.data?.units?.length,
            message: response.message,
            fullResponse: response
          });
          setChargerDetail(null);
          setError(response.message || `Charger not found. Response: ${JSON.stringify(response)}`);
        }
      } catch (err) {
        console.error('Failed to load charger detail:', err);
        console.error('Error details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load charger details');
        setChargerDetail(null);
      } finally {
        setLoading(false);
      }
    };

    fetchChargerDetail();
  }, [unitId, orgId]);

  // Set breadcrumbs when charger detail is loaded
  useEffect(() => {
    if (chargerDetail && chargerDetail.unit_name) {
      setChargerBreadcrumbs(chargerDetail.unit_name);
    }
  }, [chargerDetail, setChargerBreadcrumbs]);

  // Fetch GPS data when unitId or orgId is available - only once per unitId
  useEffect(() => {
    console.log('GPS useEffect triggered:', { unitId, orgId, user: !!user, authLoading, gpsDataFetched: gpsDataFetchedRef.current, lastFetchedUnitId: lastFetchedUnitId.current });
    
    // Wait for auth to finish loading
    if (authLoading) {
      console.log('GPS fetch skipped: auth still loading');
      return;
    }
    
    // Skip if we don't have required IDs
    if (!unitId || !orgId) {
      console.log('GPS fetch skipped: missing unitId or orgId', { unitId, orgId, user: !!user });
      return;
    }
    
    // Reset refs when unitId changes
    if (lastFetchedUnitId.current !== unitId && lastFetchedUnitId.current !== null) {
      console.log('UnitId changed from', lastFetchedUnitId.current, 'to', unitId, '- resetting GPS fetch refs');
      gpsDataFetchedRef.current = false;
      // Clear GPS data when unitId changes
      setGpsData([]);
      setGpsTimestamp(null);
      setGpsError(null);
    }
    
    // Prevent duplicate calls - only fetch if we haven't fetched for this unitId yet
    if (gpsDataFetchedRef.current && lastFetchedUnitId.current === unitId) {
      console.log('GPS data already fetched for this unitId, skipping');
      return;
    }
    
    // Also check if we're already loading
    if (loadingGPS) {
      console.log('GPS data fetch already in progress, skipping');
      return;
    }
    
    // Set ref BEFORE calling to prevent duplicate calls in StrictMode
    gpsDataFetchedRef.current = true;
    lastFetchedUnitId.current = unitId;
    
    console.log('GPS useEffect: Fetching GPS data for unitId:', unitId, 'orgId:', orgId);
    fetchGPSData();
  }, [unitId, orgId, fetchGPSData, loadingGPS, user, authLoading]);

  // Initialize map when component mounts and charger detail is loaded
  // Wait for GPS data if not available yet to center on charger location
  useEffect(() => {
    console.log('=== CHARGER DETAIL MAP USEEFFECT TRIGGERED ===');
    console.log('Map current:', map.current);
    console.log('Container current:', mapContainer.current);
    console.log('Has valid token:', hasValidToken);
    console.log('Mapbox library loaded:', typeof mapboxgl !== 'undefined');
    console.log('Mapbox access token:', mapboxgl.accessToken ? 'Set' : 'Not set');
    console.log('GPS data available:', gpsData && gpsData.length > 0);
    console.log('useEffect dependencies check - all conditions met:', {
      mapNotExists: !map.current,
      containerExists: mapContainer.current,
      hasToken: hasValidToken,
      mapboxglExists: !!mapboxgl,
      chargerDetailLoaded: !!chargerDetail,
      notLoading: !loading,
      hasGpsData: gpsData && gpsData.length > 0
    });
    
    // Only initialize map when all conditions are met
    // Wait for GPS data to be available if we don't have it yet (unless loading failed)
    if (map.current || !mapContainer.current || !hasValidToken || typeof mapboxgl === 'undefined' || !chargerDetail || loading) {
      console.log('Map initialization conditions not met, skipping');
      return;
    }
    
    // If GPS data is not available yet and we're not in an error state, wait for it
    if ((!gpsData || gpsData.length === 0) && !gpsError && loadingGPS) {
      console.log('Waiting for GPS data before initializing map center...');
      return;
    }

    // Function to initialize the map
    const initializeMap = () => {
      try {
        console.log('=== CHARGER DETAIL MAP INITIALIZATION START ===');
        const container = mapContainer.current!;
        console.log('Container element:', container);
        console.log('Container dimensions:', container.offsetWidth, 'x', container.offsetHeight);
        console.log('Container computed style:', window.getComputedStyle(container));
        console.log('Mapbox token valid:', hasValidToken);
        console.log('Mapbox token present:', MAPBOX_ACCESS_TOKEN ? 'Yes' : 'No');
        console.log('Mapbox token length:', MAPBOX_ACCESS_TOKEN?.length || 0);
        
        // Ensure container has dimensions - be more lenient for card layout
        if (container.offsetWidth === 0 || container.offsetHeight === 0) {
          console.log('❌ Container has no dimensions, retrying in 500ms...');
          setTimeout(initializeMap, 500);
          return;
        }

        // Force container to have minimum dimensions if needed
        if (container.offsetHeight < 200) {
          container.style.height = '320px';
          console.log('✅ Forced container height to 320px');
        }

        console.log('✅ Container ready, creating map...');
        console.log('Mapbox global object:', typeof mapboxgl);
        console.log('Mapbox access token set:', !!mapboxgl.accessToken);
        console.log('Mapbox access token value:', mapboxgl.accessToken ? mapboxgl.accessToken.substring(0, 10) + '...' : 'None');
        
        // Determine initial center - use GPS data if available
        // Only initialize if we have GPS data or if GPS has failed (to show error state)
        let initialCenter: [number, number];
        let initialZoom = 12;
        
        if (gpsData && gpsData.length > 0) {
          const firstLocation = gpsData[0];
          initialCenter = [firstLocation.longitude, firstLocation.latitude];
          console.log('Using charger GPS location for initial center:', initialCenter);
        } else if (gpsError) {
          // If GPS failed, use a neutral center (but map will show error state)
          // This is a fallback - ideally we'd wait for GPS data
          initialCenter = [0, 0];
          console.log('GPS error occurred, using fallback center');
        } else {
          // This shouldn't happen due to the check above, but just in case
          console.log('No GPS data and no error - this should not happen');
          initialCenter = [0, 0];
        }
        
        // Try to create a simple map first
        console.log('Creating map with basic configuration...');
        map.current = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: initialCenter,
          zoom: initialZoom,
          attributionControl: false // Disable Mapbox attribution
        });

        console.log('✅ Map object created:', map.current);

        map.current.on('load', () => {
          console.log('🎉 ChargerDetails map loaded successfully!');
          setMapError(null); // Clear any previous errors
          setMapInitializationTimeout(false); // Clear timeout flag
          
          // Trigger a resize to ensure proper rendering in the card layout
          setTimeout(() => {
            if (map.current) {
              map.current.resize();
              console.log('✅ Map resized after load');
              
              // Add markers after map is fully loaded
              if (gpsData && gpsData.length > 0) {
                console.log('Adding markers after map load...');
                addMarkersToMap();
              }
            }
          }, 100);
        });

        map.current.on('error', (e) => {
          console.error('❌ Map error event:', e);
          if (e.error && e.error.message) {
            setMapError(`Map error: ${e.error.message}`);
          } else {
            setMapError('An error occurred while loading the map');
          }
        });

        // Don't update state on move to prevent re-initialization
        // The map will maintain its own state internally

        console.log('✅ Navigation controls removed - using custom controls instead');
        
        console.log('✅ Map initialization complete - waiting for load event...');
        
        // Add a timeout to check if load event never fires
        setTimeout(() => {
          if (map.current && !map.current.loaded()) {
            console.warn('⚠️ Map load event did not fire within 5 seconds');
            console.log('Map loaded status:', map.current.loaded());
            console.log('Map style loaded:', map.current.isStyleLoaded());
          }
        }, 5000);
        
        console.log('=== CHARGER DETAIL MAP INITIALIZATION END ===');
      } catch (error) {
        console.error('❌ Error initializing map:', error);
        console.error('Error details:', error);
        setMapError(`Failed to initialize map: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    // Start initialization with a longer delay to ensure DOM is ready and container is rendered
    const timer = setTimeout(initializeMap, 1000); // Increased delay
    
    // Set a timeout to prevent infinite loading
    const timeoutTimer = setTimeout(() => {
      if (!map.current) {
        console.error('Map initialization timeout after 15 seconds');
        setMapInitializationTimeout(true);
        setMapError('Map initialization timed out. Please refresh the page.');
      }
    }, 15000); // Increased timeout

    return () => {
      clearTimeout(timer);
      clearTimeout(timeoutTimer);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [hasValidToken, chargerDetail, loading, gpsData, gpsError, loadingGPS, addMarkersToMap]); // Depend on GPS data to center on charger location

  // Effect to add markers when GPS data changes
  useEffect(() => {
    console.log('=== MARKER USEEFFECT TRIGGERED ===');
    console.log('map.current:', map.current);
    console.log('gpsData:', gpsData);
    console.log('gpsData.length:', gpsData?.length);
    
    if (map.current && gpsData && gpsData.length > 0) {
      console.log('✅ Conditions met, calling addMarkersToMap');
      addMarkersToMap();
    } else {
      console.log('❌ Conditions not met for adding markers');
    }
  }, [addMarkersToMap, gpsData, map]);

  // Effect to resize map when component becomes visible
  useEffect(() => {
    if (map.current && !loading && chargerDetail) {
      // Small delay to ensure the container is fully rendered
      const timer = setTimeout(() => {
        if (map.current) {
          map.current.resize();
          console.log('Map resized after component mount');
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [loading, chargerDetail]);

  // Debug effect to check container status
  useEffect(() => {
    console.log('=== CHARGER DETAIL COMPONENT RENDERED ===');
    console.log('chargerDetail:', chargerDetail);
    console.log('loading:', loading);
    console.log('mapContainer.current:', mapContainer.current);
    
    if (mapContainer.current) {
      console.log('Map container ref is set:', mapContainer.current);
      console.log('Container dimensions:', mapContainer.current.offsetWidth, 'x', mapContainer.current.offsetHeight);
    } else {
      console.log('Map container ref is not set yet');
    }
  }, [chargerDetail, loading]);

  // Only render content if we have charger data that matches the current unitId
  // Otherwise show loading or error states
  const hasMatchingChargerData = chargerDetail && chargerDetail.unit_id === unitId;
  
  if (!hasMatchingChargerData) {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading charger details...</Typography>
        </Box>
      );
    }
    
    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography>Charger {unitId} could not be loaded</Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Charger {unitId} not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: (theme) => theme.palette.background.default, 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* Main content */}
      <Box sx={{ flex: 1, pr: 3, pl: 3, pb: 3 }}>
        {/* Static content - memoized to prevent re-renders */}
        <ChargerStaticContent
          chargerDetail={chargerDetail!}
          user={user}
          orgId={orgId}
          mapContainer={mapContainer}
          map={map}
          mapError={mapError}
          mapInitializationTimeout={mapInitializationTimeout}
          hasValidToken={hasValidToken}
          loadingGPS={loadingGPS}
          gpsError={gpsError}
          gpsData={gpsData}
          gpsTimestamp={gpsTimestamp}
          mapControls={mapControls}
          handleMapControl={handleMapControl}
          addMarkersToMap={addMarkersToMap}
          onMapRetry={handleMapRetry}
        />

        {/* Charger Navigation Bar */}
        <Box sx={{ 
          ml: -3, // Remove left margin to align with sidebar
          mr: -3, // Remove right margin to extend to edge
          mb: 3,
        }}>
          <ChargerNavigationBar />
        </Box>

        {/* Content Area - Renders nested routes */}
        <Box sx={{ 
          flex: 1,
          p: 0,
          overflow: 'auto'
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default ChargerDetails;
